export const OTP_ERROR_KEYS = {
  invalidPhone: 'Enter a valid mobile phone number',
  invalidCode: 'Invalid or expired verification code',
  throttled: 'OTP request throttled',
  smsUnavailable: 'SMS service unavailable',
  verificationRequired: 'Phone verification required',
  alreadyRegistered: 'Phone already registered',
  setupExpired: 'Your session has expired, please try again',
  wrongPassword: 'Incorrect password',
  emailConfirmUnavailable: 'This email is already confirmed, or there is no email on file to confirm',
  // WP3 §3 + WP1 handoff: checkout-time gates. currentAccessRemains and
  // promoNotEligible are the two new 4005xx codes; emailVerificationRequiredForCheckout
  // is the raised (presence -> emailApproved) checkout email gate.
  emailVerificationRequiredForCheckout: 'Please verify your email address before checking out',
  currentAccessRemains: 'Your current subscription access remains active',
  promoNotEligible: 'The selected subscription option changed. Review the current details and select again.',
  // Post-pending pipeline handoff §1.4/§4: 400515 checkout standing gate
  // (three reason variants) and the 403013 admin account block.
  approvalRequiredForPaidPlan: 'This plan requires an approved application',
  approvalMissingBlocksCheckout: 'Submit your professional information for review before starting a subscription',
  approvalRejectedBlocksCheckout: 'View the review note and update your information to continue with a subscription',
  approvalStatusChanged: 'Your application status changed. Review its current status to continue.',
  accountBlockedContactUs: 'Your account is blocked. Please contact us to resolve this.',
  // L5: the reviewer-facing half of the rejection-comment requirement. The
  // admin list already blocks an empty reason client-side, so 400026 only
  // surfaces when the two disagree — a stale tab, a direct API call, or a
  // whitespace-only reason that passed a laxer local check. Without a mapping
  // the reviewer saw the server's raw English sentence in a Turkish UI.
  rejectionReasonRequired: 'A rejection reason is required',
}

export const OTP_ERROR_KEYS_BY_CODE = {
  400007: OTP_ERROR_KEYS.wrongPassword,
  400015: OTP_ERROR_KEYS.invalidPhone,
  400016: OTP_ERROR_KEYS.verificationRequired,
  400017: OTP_ERROR_KEYS.alreadyRegistered,
  400018: OTP_ERROR_KEYS.invalidCode,
  400023: OTP_ERROR_KEYS.emailConfirmUnavailable,
  400024: OTP_ERROR_KEYS.emailVerificationRequiredForCheckout,
  400026: OTP_ERROR_KEYS.rejectionReasonRequired,
  400513: OTP_ERROR_KEYS.currentAccessRemains,
  400514: OTP_ERROR_KEYS.promoNotEligible,
  400515: OTP_ERROR_KEYS.approvalStatusChanged,
  400516: 'freeAccess.errors.notEligible',
  401018: OTP_ERROR_KEYS.setupExpired,
  403013: OTP_ERROR_KEYS.accountBlockedContactUs,
  503001: OTP_ERROR_KEYS.smsUnavailable,
}

// 400515 carries a top-level `reason`; a code→key map cannot express its
// three meanings, so checkout resolves the reason first and falls back to
// the 400515 entry above when the reason is absent or unknown.
export const OTP_ERROR_KEYS_BY_REASON_400515 = {
  'approval-required-for-paid-plan': OTP_ERROR_KEYS.approvalRequiredForPaidPlan,
  'approval-missing': OTP_ERROR_KEYS.approvalMissingBlocksCheckout,
  'approval-rejected': OTP_ERROR_KEYS.approvalRejectedBlocksCheckout,
}

export const OTP_UI_KEYS = {
  phoneNumber: 'Phone Number',
  rememberMe: 'Remember Me',
  phoneVerification: 'Phone Verification',
  sendCode: 'Send Verification Code',
  verificationCode: 'Verification Code',
  verify: 'Verify',
  resend: 'Resend Code',
  cancel: 'Cancel',
  codeSent: 'Verification code sent',
  phoneVerified: 'Phone verified',
  verifyPhone: 'Verify phone',
  changePhone: 'Change phone',
  addPhone: 'Add a phone number',
  notConfirmed: 'Your phone is not confirmed.',
  noPhoneYet: 'You have not added a phone number yet.',
  resendIn: 'You can request a new code in {seconds} seconds.',
  enterCode: 'Enter the six-digit code sent to your phone.',
  expiresIn: 'The code expires in {seconds} seconds.',
  // Registration-lane email confirm codes live for an hour, so their countdown
  // takes a preformatted mm:ss / h:mm:ss value instead of raw seconds. The
  // seconds variant above stays: the phone flows still count down 5 minutes.
  expiresInTime: 'The code expires in {time}.',
  codeExpired: 'The code has expired. Request a new one.',
  confirmPasswordPrompt: 'Enter your account password to confirm this change.',
  password: 'Password',
  // WP2: the account page now ADOPTS a live email-confirm challenge instead of
  // minting a fresh one on open. Both strings below exist so the two states of
  // that section are unambiguous: one explains why no input is shown yet, the
  // other warns that an explicit resend voids the code already in the inbox.
  noActiveEmailCode: 'No verification code is waiting. Send one to your email address to continue.',
  resendVoidsPreviousCode: 'Sending a new code makes the code already in your inbox stop working.',
}

export const OTP_I18N_KEYS = [
  ...Object.values(OTP_UI_KEYS),
  ...Object.values(OTP_ERROR_KEYS),
]

export default OTP_I18N_KEYS

export const OTP_ERROR_KEYS_BY_REASON_400516 = {
  'approval-pending': 'freeAccess.errors.approvalPending',
  'approval-required': 'freeAccess.errors.approvalRequired',
  'already-granted': 'freeAccess.errors.alreadyGranted',
  'sysadmin-excluded': 'freeAccess.errors.notEligible',
  'subscription-history': 'freeAccess.errors.notEligible',
  'promotion-redeemed': 'freeAccess.errors.notEligible',
}
export const freeAccessClaimErrorKey = error => {
  const data = (error && error.response && error.response.data) || {}
  if (Number(data.errorCode) === 400516) return OTP_ERROR_KEYS_BY_REASON_400516[data.reason] || 'freeAccess.errors.notEligible'
  return OTP_ERROR_KEYS_BY_CODE[data.errorCode] || 'An error occurred. Please try again later.'
}
